import React from 'react';
import { TFunction } from 'i18next';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { toast } from 'react-toastify';
import { CustomErrorToast } from '@shared/components/customErrorToast';
import {
  ConflictAgentReferenceDto,
  ConflictWithAgentsErrorDto,
} from '@shared/services/apiService/missing-types';
import { formatTruncatedList } from '@shared/utils/formatList';

export const MAX_AGENTS_IN_CONFLICT_MESSAGE = 5;

export const formatAgentsUsingSetting = (
  agents: ConflictAgentReferenceDto[],
  t: TFunction,
): string =>
  formatTruncatedList(
    agents.map((agent) =>
      agent.companyName ? `${agent.name} (${agent.companyName})` : agent.name,
    ),
    (key, params) => t(key, params),
    MAX_AGENTS_IN_CONFLICT_MESSAGE,
  );

const getAgentsConflict = (error: unknown): ConflictWithAgentsErrorDto | null => {
  const fetchError = error as FetchBaseQueryError;
  if (
    typeof fetchError?.status === 'number' &&
    fetchError.status === 409 &&
    fetchError.data &&
    typeof fetchError.data === 'object' &&
    'metadata' in fetchError.data &&
    Array.isArray((fetchError.data as ConflictWithAgentsErrorDto).metadata?.agents)
  ) {
    return fetchError.data as ConflictWithAgentsErrorDto;
  }
  return null;
};

/**
 * Shows the "used by agents" toast for a 409 from deleting a voice or transcriber.
 * Returns false for any other error so the caller keeps its own handling.
 */
export const showAgentsUsingSettingConflictToast = (
  error: unknown,
  t: TFunction,
  messageKey: string,
): boolean => {
  const conflict = getAgentsConflict(error);
  if (!conflict) return false;

  const message = t(messageKey, { agents: formatAgentsUsingSetting(conflict.metadata.agents, t) });

  toast.error((props) => <CustomErrorToast {...props} message={message} />, {
    closeButton: false,
    autoClose: 8000,
    icon: false,
  });
  return true;
};
